import type { ComponentRef, ViewContainerRef } from '@angular/core';
import { inject } from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';

import type { Observable } from 'rxjs';
import { combineLatest, map, startWith, takeUntil } from 'rxjs';

import { setInputs } from '@core/common-legacy';
import type { FilterPayload, ViewFilterInput, ViewFilterInputDef } from '@core/views-types';

import { FiltersFormStateService } from '../../services/filters-form-state.service';
import { OptionsService } from '../options.service';
import type { InputComponentContext, InputService } from '../types';
import { FilterEvents } from '../types';
import type { MultiSelectInputState } from './input-multiselect.component';
import { InputMultiselectComponent, optionToMultiSelectItem } from './input-multiselect.component';
import type {
  InputMultiSelectFormValue,
  InputMultiSelectOptions,
  InputMultiSelectSavedValue,
} from './types';

export interface FilterValues {
  fullValue: FilterPayload;
  skipSelfValue: FilterPayload;
}

export class InputMultiSelectService implements InputService {
  private optionsService = inject(OptionsService);
  private filterStateService = inject(FiltersFormStateService);

  public savedToFormValue(input: ViewFilterInput<InputMultiSelectSavedValue>) {
    return <InputMultiSelectFormValue>input.value.map(option => ({
      ...option.value,
      type: option.type,
    }));
  }

  public getComponentState(
    input: ViewFilterInput<InputMultiSelectSavedValue>,
  ): MultiSelectInputState {
    return input.value.map(option => optionToMultiSelectItem(option));
  }

  public getInputControl(inputDef: ViewFilterInputDef): InputMultiselectComponent['inputControl'] {
    const validators = Validators.compose([inputDef.required ? Validators.required : null]);
    return new FormControl(null, validators);
  }

  public createInputComponent(
    vcRef: ViewContainerRef,
    destroy$: Observable<void>,
    context: InputComponentContext,
  ): ComponentRef<InputMultiselectComponent> {
    const inputOptionsId = this.optionsService.getInputOptionsId(context);
    const componentRef = vcRef.createComponent(InputMultiselectComponent);
    const mode = context.origin === 'quick' && context.inputs.length === 1 ? 'quick' : 'advanced';

    this.updateInputs(componentRef, { mode, placeholder: context.inputDef.props.placeholder });

    const subscriptions$ = this.optionsService.getSubscriptionsChanges(context, destroy$);
    this.optionsService.handleLoadOptions(
      context,
      destroy$,
      subscriptions$,
      componentRef.instance.search,
    );
    this.optionsService.handleLoadMore(
      context,
      destroy$,
      subscriptions$,
      componentRef.instance.loadMore,
    );
    this.watchOptions(inputOptionsId, destroy$, componentRef);
    this.publishFilterEvents(context, destroy$, componentRef.instance);
    this.validateOptions(context, destroy$);

    return componentRef;
  }

  public getInputLabel(
    context: InputComponentContext,
    control: InputMultiselectComponent['inputControl'],
    inputState$: Observable<MultiSelectInputState>,
  ) {
    return combineLatest([control.valueChanges.pipe(startWith(control.value)), inputState$]).pipe(
      map(([controlValue, inputState]) => {
        if (!controlValue || !controlValue.length) return '';

        return inputState?.map(item => item.label).join(', ') ?? '';
      }),
    );
  }

  public updateInputs(
    componentRef: ComponentRef<InputMultiselectComponent>,
    inputs: Partial<
      Pick<
        InputMultiselectComponent,
        'inputControl' | 'state' | 'optionsData' | 'mode' | 'placeholder'
      >
    >,
  ) {
    setInputs(componentRef, inputs);
    componentRef.changeDetectorRef.detectChanges();
  }

  private publishFilterEvents(
    context: InputComponentContext,
    destroy$: Observable<void>,
    component: InputMultiselectComponent,
  ) {
    const {
      inputDef: { id: inputId },
      filterEvents,
    } = context;
    outputToObservable(component.closed)
      .pipe(takeUntil(destroy$))
      .subscribe(() => filterEvents.next({ inputId, event: FilterEvents.InputBlur }));
    outputToObservable(component.chipRemoved)
      .pipe(takeUntil(destroy$))
      .subscribe(() => filterEvents.next({ inputId, event: FilterEvents.ChipRemove }));
    outputToObservable(component.clear)
      .pipe(takeUntil(destroy$))
      .subscribe(() => filterEvents.next({ inputId, event: FilterEvents.Clear }));
  }

  private watchOptions(
    inputOptionsId: string,
    destroy$: Observable<void>,
    componentRef: ComponentRef<InputMultiselectComponent>,
  ) {
    this.optionsService
      .getOptions<InputMultiSelectOptions>(inputOptionsId)
      .pipe(takeUntil(destroy$))
      .subscribe((options: InputMultiselectComponent['optionsData']) => {
        this.updateInputs(componentRef, { optionsData: options });
      });
  }

  private validateOptions(context: InputComponentContext, destroy$: Observable<void>) {
    const filterControl = this.filterStateService.getFilterControl(context.uuid)!;
    const inputIndex = this.optionsService.getInputIndex(context.inputs, context.inputDef.id);
    const inputControl = <InputMultiselectComponent['inputControl']>(
      filterControl.controls.value.controls[inputIndex]
    );
    this.optionsService
      .validateOptions<InputMultiselectComponent, InputMultiSelectOptions>(
        context,
        filterControl,
        inputControl => !!inputControl.value?.length,
      )
      .pipe(takeUntil(destroy$))
      .subscribe(options => {
        const state = options.map(option => optionToMultiSelectItem(option));
        const { inputState } = this.filterStateService.getFilterData(filterControl);
        const newState = [...inputState];
        newState.splice(inputIndex, 1, state);
        inputControl.setValue(state.map(item => item.value));
        this.filterStateService.updateFilterState(context.uuid, newState);
      });
  }
}
